import { userIdZodSchema } from "@@/shared/users/user-id";
import type { UUID } from "@@/shared/uuid";
import type { Except } from "type-fest";
import { z } from "zod";
import { signatureFactory } from "../../signature-factory";
import type {
    _OrderEventBaseFactoryParams} from "../_order-base.event";
import {
    _orderEventBaseFactory,
    _orderEventBaseZodObject,
} from "../_order-base.event";
import { orderEventIdZodSchema } from "../_order-event-id";

const orderEventPaymentFailedZodObject = _orderEventBaseZodObject.merge(
    z.object({
        type: z.literal("PAYMENT_FAILED"),
        createdBy: userIdZodSchema.or(z.string()),
        paymentUUID: z.string() as unknown as z.ZodType<UUID>,
        reason: z.string(),
        reasonCode: z.string(),
        paymentFor: z.object({
            orderEventId: orderEventIdZodSchema,
        }),
    }),
);
type OrderPaymentFailedEvent = z.infer<typeof orderEventPaymentFailedZodObject>;
const orderEventPaymentFailedZodSchema =
    orderEventPaymentFailedZodObject as z.ZodSchema<OrderPaymentFailedEvent>;

type FactoryParams = _OrderEventBaseFactoryParams &
    Except<
        OrderPaymentFailedEvent,
        "type" | "_id" | "_type" | "_createdAt" | "signature"
    >;

type Params = Except<FactoryParams, "signature">;

const orderEventPaymentFailedFactory = (
    params: Params,
): OrderPaymentFailedEvent => ({
    ..._orderEventBaseFactory({
        orderId: params.orderId,
        orderGroupId: params.orderGroupId,
        _version: params._version,
        providerId: params.providerId,
        providerSlug: params.providerSlug,
        signature: signatureFactory(params.orderId, "PAYMENT_FAILED", [
            params.paymentUUID,
        ]),
    }),
    type: "PAYMENT_FAILED",
    createdBy: params.createdBy,
    paymentUUID: params.paymentUUID,
    reason: params.reason,
    reasonCode: params.reasonCode,
    paymentFor: params.paymentFor,
});

const isOrderPaymentFailedEvent = (
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    event: any,
): event is OrderPaymentFailedEvent => {
    return (
        !!event &&
        event._type === "ORDER_EVENT" &&
        event?.type === "PAYMENT_FAILED"
    );
};

export {
    isOrderPaymentFailedEvent,
    orderEventPaymentFailedFactory,
    orderEventPaymentFailedZodObject,
    orderEventPaymentFailedZodSchema,
};
export type { OrderPaymentFailedEvent };
